import * as THREE from "three";
import { RESPAWN_ZONE_CONFIG } from "./config";
import { createExplosion } from "./effects/Explosion";
import { createLightningExplosion } from "./effects/LightningExplosion";
import { createLaserExplosion } from "./effects/LaserExplosion";
import { Tank } from "./Tank";
import { removeDamageSmoke } from "./damage";
import { TankModelConfig, TANK_MODELS } from "./tanks/TankModel";

export class TankSpawning {
  private tank: Tank;
  private destroyedEffects: THREE.Object3D[] = [];
  private invulnerableUntil: number = 0;

  constructor(tank: Tank) {
    this.tank = tank;
  }

  // Metoda do respawnu czołgu
  spawn() {
    console.log(`[TankSpawning] Spawning tank ${this.tank.playerId}`);

    // Reset state
    this.tank.isDestroyed = false;
    this.tank.health = this.tank.maxHealth;
    this.tank.currentSpeed = 0;
    this.tank.currentTurnSpeed = 0;
    this.tank.damageSmoke = false;

    // Reset appearance
    if (this.tank.body) {
      (this.tank.body.material as THREE.MeshStandardMaterial).color.setHex(0x5d7f38);
      this.tank.body.position.y = 0.6; // Reset tank body height
    }

    // Clean up destruction effects and smoke
    this.cleanupDestroyedEffects();
    removeDamageSmoke(this.tank); // Add this line to ensure smoke effect is cleaned up

    // Get random respawn point
    const environmentProvider = this.tank.environment ?? window.gameState?.environmentService;
    if (environmentProvider) {
      try {
        const respawnPoint = environmentProvider.getRandomRespawnPoint();
        if (respawnPoint) {
          // Ustaw pozycję i rotację czołgu
          this.tank.mesh.position.copy(respawnPoint.position);
          this.tank.mesh.rotation.y = respawnPoint.rotation;
          console.log(`[TankSpawning] Tank ${this.tank.playerId} respawning at:`, {
            position: this.tank.mesh.position.toArray(),
            rotation: this.tank.mesh.rotation.y,
          });

          // Wyślij aktualizację pozycji do serwera
          if (window.socket) {
            window.socket.emit("tankPosition", {
              playerId: this.tank.playerId,
              position: this.tank.mesh.position.toArray(),
              rotation: this.tank.mesh.rotation.y,
            });
            console.log(`[TankSpawning] Sent respawn position to server for tank ${this.tank.playerId}`);
          }
        } else {
          console.warn(`[TankSpawning] No valid respawn point found for tank ${this.tank.playerId}`);
        }
      } catch (error) {
        console.error(`[TankSpawning] Error getting respawn point for tank ${this.tank.playerId}:`, error);
      }
    }

    // Set temporary invulnerability
    this.invulnerableUntil = Date.now() + RESPAWN_ZONE_CONFIG.SAFE_TIME;

    console.log(`[TankSpawning] Tank ${this.tank.playerId} spawned successfully`);
  }

  // Metoda do obsługi zniszczenia czołgu
  //destroy(shooterTankModel: Tank["modelId"]) {
  destroy() {
    console.log(`[TankSpawning] Starting tank destruction process for tank ${this.tank.playerId}:`, {
      tankHealth: this.tank.health,
      isDestroyed: this.tank.isDestroyed,
      hasDamageSmoke: this.tank.damageSmoke,
      //shooterTankModel: shooterTankModel,
    });
    let shooterTankModel = "standard";
    // Reset controls
    this.tank.controls = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      handbrake: false,
      barrelUp: false,
      barrelDown: false,
    };

    //const projectileType = this.tank.projectileType;

    // Change appearance
    if (this.tank.body) {
      (this.tank.body.material as THREE.MeshStandardMaterial).color.setHex(0x666666);
      this.tank.body.position.y -= 0.3; // Lower the tank body
      console.log(`[TankSpawning] Changed tank ${this.tank.playerId} appearance`);
    }

    // Create smoke and explosion effects
    if (window.gameState?.scene) {
      try {
        console.log(`[TankSpawning] Creating destruction effects for tank ${this.tank.playerId}`);

        // Create explosion and add to scene (it applies explosion EFFECT DESTROYED TANK HAS NOT SHOOTING ONE!)
        // Zawsze tworzymy efekt wybuchu - domyślnie standardowy
        let explosionEffect: THREE.Object3D | null = null;
        const tankModelId = this.tank.modelId || "standard";

        console.log(`[TankSpawning] Creating explosion for tank ${this.tank.playerId}, modelId: ${tankModelId}`);

        if (tankModelId === "light" || shooterTankModel === "light") {
          explosionEffect = createLightningExplosion(window.gameState.scene, this.tank.mesh.position, 20);
        } else if (tankModelId === "heavy" || shooterTankModel === "heavy") {
          explosionEffect = createLaserExplosion(window.gameState.scene, this.tank.mesh.position, 20);
        } else {
          // Domyślny wybuch dla "standard" i innych
          explosionEffect = createExplosion(window.gameState.scene, this.tank.mesh.position, 20);
        }

        if (explosionEffect && explosionEffect.isObject3D) {
          this.destroyedEffects.push(explosionEffect);
          console.log(`[TankSpawning] Created explosion effect for tank ${this.tank.playerId}`);
        } else {
          console.warn(`[TankSpawning] Failed to create explosion effect for tank ${this.tank.playerId}`);
        }

        console.log(`[TankSpawning] Setting damage smoke for tank ${this.tank.playerId}:`, {
          currentSmoke: this.tank.damageSmoke,
          tankHealth: this.tank.health,
          isDestroyed: this.tank.isDestroyed,
        });

        // Add smoke effect
        this.tank.damageSmoke = true;
        console.log(
          `[TankSpawning] Damage smoke status updated for tank ${this.tank.playerId}:`,
          this.tank.damageSmoke
        );
      } catch (error) {
        console.error(`[TankSpawning] Error creating destruction effects for tank ${this.tank.playerId}:`, error);
      }
    }

    // Set destroyed state
    this.tank.isDestroyed = true;

    // Emit destroyed event with playerId only
    if (window.socket) {
      window.socket.emit("tankDestroyed", { playerId: this.tank.playerId });
      console.log(`[TankSpawning] Emitted tankDestroyed event for playerId: ${this.tank.playerId}`);
    }

    console.log(`[TankSpawning] Tank ${this.tank.playerId} destruction process completed:`, {
      isDestroyed: this.tank.isDestroyed,
      hasDamageSmoke: this.tank.damageSmoke,
      finalHealth: this.tank.health,
    });
  }

  // Metoda do czyszczenia efektów zniszczenia
  cleanupDestroyedEffects() {
    // Remove all effects from scene and dispose resources
    this.destroyedEffects.forEach((effect) => {
      // Traverse through all children of the effect
      effect.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Dispose of geometry
          if (child.geometry) {
            child.geometry.dispose();
          }
          // Dispose of material and its textures
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => {
                if (material.map) material.map.dispose();
                material.dispose();
              });
            } else {
              if (child.material.map) child.material.map.dispose();
              child.material.dispose();
            }
          }
        } else if (child instanceof THREE.Light) {
          // Handle any light-specific cleanup if needed
          if (child.shadow && child.shadow.map) {
            child.shadow.map.dispose();
          }
        }
      });

      // Remove from scene if still attached
      if (effect.parent) {
        effect.parent.remove(effect);
      }
    });

    // Clear effects array
    this.destroyedEffects = [];
    console.log(`[TankSpawning] Cleaned up destruction effects and disposed resources for tank ${this.tank.playerId}`);
  }

  // Metody pomocnicze
  isInSafeTime(): boolean {
    return Date.now() < this.invulnerableUntil;
  }
}
